import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wallet, Users, AlertCircle, Inbox, Search, Settings2, ShoppingCart, ListChecks, Package, UserCheck, Receipt } from "lucide-react";
import { Money } from "@/components/wallet/shared";
import OwnerWalletDialog from "@/components/wallet/OwnerWalletDialog";
import WalletConfigDialog from "@/components/wallet/WalletConfigDialog";
import ChargeDialog from "@/components/wallet/ChargeDialog";
import ItemsDialog from "@/components/wallet/ItemsDialog";
import PackagesDialog from "@/components/wallet/PackagesDialog";
import { CashiersDialog } from "@/components/wallet/CashiersDialog";
import { fmtDate } from "@/lib/format";

function Kpi({ icon: Icon, label, children, testId }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Icon size={13}/>{label}</div>
      <div className="text-2xl font-semibold mt-1" data-testid={testId}>{children}</div>
    </Card>
  );
}

export default function WalletAdmin() {
  const [ownerType, setOwnerType] = useState("student");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);

  const { data: summary } = useQuery({ queryKey: ["wallet-summary"], queryFn: async () => (await api.get("/wallet/summary")).data });
  const { data: owners = [], isLoading } = useQuery({
    queryKey: ["wallet-owners", ownerType],
    queryFn: async () => (await api.get("/wallet/owners", { params: { owner_type: ownerType } })).data,
  });
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? owners.filter((o) => `${o.name} ${o.matricola || ""} ${o.email || ""}`.toLowerCase().includes(s)) : owners;
  }, [owners, q]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Wallet"
        subtitle="Credito virtuale in euro per studenti e docenti. Ricariche, accrediti e addebiti."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button className="rounded-full bg-[#E2725B] hover:bg-[#C95F49]" onClick={() => setChargeOpen(true)} data-testid="wallet-charge-btn"><ShoppingCart size={14} className="mr-1.5"/> Addebita acquisto</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setItemsOpen(true)} data-testid="wallet-items-btn"><ListChecks size={14} className="mr-1.5"/> Listino</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setPkgOpen(true)} data-testid="wallet-packages-btn"><Package size={14} className="mr-1.5"/> Pacchetti</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setCashOpen(true)} data-testid="wallet-cashiers-btn"><UserCheck size={14} className="mr-1.5"/> Cassieri bar</Button>
            <Link to="/wallet/bar"><Button variant="outline" className="rounded-full" data-testid="wallet-bar-report-link"><Receipt size={14} className="mr-1.5"/> Incassi bar</Button></Link>
            <Button variant="outline" className="rounded-full" onClick={() => setCfgOpen(true)} data-testid="wallet-config-btn"><Settings2 size={14} className="mr-1.5"/> Impostazioni wallet</Button>
            <Link to="/wallet/ricariche"><Button className="rounded-full bg-[#0F766E] hover:bg-[#115E59]" data-testid="wallet-topups-link"><Inbox size={14} className="mr-1.5"/> Richieste ({summary?.pending_topups ?? 0})</Button></Link>
          </div>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Wallet} label="Credito totale" testId="wallet-kpi-total"><Money value={summary?.total_balance || 0}/></Kpi>
        <Kpi icon={Users} label="Wallet attivi" testId="wallet-kpi-active">{summary?.wallets_with_movements ?? 0}</Kpi>
        <Kpi icon={AlertCircle} label="Saldi negativi" testId="wallet-kpi-negative">{summary?.negative_count ?? 0}</Kpi>
        <Kpi icon={Inbox} label="Ricariche in attesa" testId="wallet-kpi-pending"><Money value={summary?.pending_amount || 0}/></Kpi>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-3">
        <Tabs value={ownerType} onValueChange={setOwnerType}>
          <TabsList>
            <TabsTrigger value="student" data-testid="wallet-tab-students">Studenti</TabsTrigger>
            <TabsTrigger value="teacher" data-testid="wallet-tab-teachers">Docenti</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, matricola, email" className="pl-8" data-testid="wallet-search"/>
        </div>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
        {isLoading && <div className="p-8 text-center text-sm text-stone-400">Caricamento…</div>}
        {!isLoading && filtered.length === 0 && <div className="p-8 text-center text-sm text-stone-400">Nessun risultato.</div>}
        <ul className="divide-y divide-stone-100">
          {filtered.map((o) => (
            <li key={o.owner_id}>
              <button type="button" onClick={() => setSelected(o)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-stone-50 transition-colors" data-testid={`wallet-owner-${o.owner_id}`}>
                <div className="w-9 h-9 rounded-full bg-[#0F766E]/10 text-[#0F766E] grid place-items-center text-sm font-semibold shrink-0">{(o.name || "?").slice(0, 1)}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{o.name}</div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {o.matricola || o.email || "—"}{o.last_at ? ` · ultimo movimento ${fmtDate(o.last_at.slice(0, 10))}` : " · nessun movimento"}
                  </div>
                </div>
                <Money value={o.balance} className="font-semibold"/>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <OwnerWalletDialog owner={selected} onClose={() => setSelected(null)}/>
      <WalletConfigDialog open={cfgOpen} onOpenChange={setCfgOpen}/>
      <ChargeDialog open={chargeOpen} onOpenChange={setChargeOpen}/>
      <CashiersDialog open={cashOpen} onOpenChange={setCashOpen}/>
      <ItemsDialog open={itemsOpen} onOpenChange={setItemsOpen}/>
      <PackagesDialog open={pkgOpen} onOpenChange={setPkgOpen}/>
    </div>
  );
}
